import {
  Modal,
  ModalFooter,
  ModalHeader,
  useBreakpoint
} from '@cencosud-cencommerce/paris-uikit';

import { useAppDispatch, useAppSelector } from '@/Application/Hooks/Redux';
import useBuyNowCart, {
  selectBuyNowCartData,
  selectBuyNowPaymentError
} from '@/Application/Selectors/buyNow/useBuyNowCart';
import { useBuyNowSkuHistory } from '@/Application/Selectors/buyNow/useBuyNowSkuHistory';
import { URL_ROUTES } from '@/constants';
import { store } from '@/Infrastructure/State';
import { BuyNowErrorCode } from '@/modules/buy-now/components/modals/BuyNowErrorCode';
import { BuyNowModalContent } from '@/modules/buy-now/components/modals/BuyNowModalContent';
import { useBuyNowClearPaymentMethod } from '@/modules/buy-now/hooks/useBuyNowClearPaymentMethod';
import { getShippingGrouping } from '@/modules/buy-now/infrastructure/BuyNowReduxRepository';
import {
  setBuyNowPaymentError,
  setIsProcessingPayment
} from '@/modules/buy-now/infrastructure/slices/BuyNowSlice';
import {
  ECta,
  errorCodeCHKCOUPON001,
  Type
} from '@/modules/buy-now/utils/error-handler/buy-now-error-handler.constants';
import { redirectToUrl } from '@/modules/perseo-billing/utils/window';
import { useSendBuyNowErrorEvent } from '@/modules/shared/utils/gtmEvents/events/useSendBuyNowErrorEvent';

export const BuyNowModalPaymentFallback = () => {
  const dispatch = useAppDispatch();
  const paymentError = useAppSelector(selectBuyNowPaymentError);
  const cart = useAppSelector(selectBuyNowCartData);
  const cartId = cart?.id;

  const { clearPayment } = useBuyNowClearPaymentMethod();
  const { refetch } = useBuyNowCart();
  const { isMobile } = useBreakpoint();
  const { skuHistory } = useBuyNowSkuHistory();

  const innerCustomCode = paymentError?.errorCode ?? '';

  useSendBuyNowErrorEvent({
    paymentError
  });

  const refetchShippingServices = async () => {
    // Usar el SKU específico de BuyNow, NO el productList del grupo
    const currentSku = skuHistory?.sku;
    const clientPreference = skuHistory?.clientPreference?.[0];

    if (!currentSku || !clientPreference || !cartId) return;

    // Llamar al grouping con el SKU específico de BuyNow
    await store.dispatch(
      getShippingGrouping({
        params: [
          {
            sku: currentSku,
            clientPreference: [clientPreference]
          }
        ],
        cartId
      })
    );
  };

  const handleModalClose = async () => {
    clearPayment();
    dispatch(setBuyNowPaymentError(null));
    dispatch(setIsProcessingPayment(false));

    // Si es un error de tipo REDIRECT, recargar servicios en lugar de redirigir
    if (paymentError?.type === Type.REDIRECT) {
      await refetchShippingServices();
      return; // No continuar con la redirección
    }

    const cta = paymentError?.cta;
    const isWhatsAppLink = cta === ECta.IR_AL_WSP_PARIS;
    if (isWhatsAppLink) {
      window.open(URL_ROUTES.WHATSAPP_LINK, '_blank');
    }

    if (paymentError?.redirectUrl) {
      redirectToUrl(paymentError?.redirectUrl);
    }

    if (innerCustomCode === errorCodeCHKCOUPON001 && cartId) {
      refetch(cartId);
    }
  };

  const hasPaymentError = !!paymentError;

  return (
    <Modal
      size="sm"
      isOpen={hasPaymentError}
      onClose={() => handleModalClose()}
    >
      <ModalHeader onClose={() => handleModalClose()} title="" />
      <div className="co-flex co-flex-col co-h-full co-bg-white co-pt-2 co-px-6 co-pb-6 co-overflow-y-auto">
        <div className="co-relative co-pt-2 tablet:co-pt-14 co-flex co-flex-col co-h-full co-w-full co-px-4 co-pb-6 tablet:co-pb-32 co-justify-between">
          {/* Desktop: Error code at top left */}
          {!isMobile && innerCustomCode && (
            <div className="co-absolute co-top-0 co-left-0 co-p-16px tablet:co-p-24px">
              <BuyNowErrorCode error={innerCustomCode} />
            </div>
          )}

          <BuyNowModalContent />

          {/* Mobile: Error code at middle bottom */}
          {isMobile && innerCustomCode && (
            <div className="co-flex co-justify-center co-w-full co-mt-auto co-mb-16px">
              <BuyNowErrorCode error={innerCustomCode} />
            </div>
          )}
        </div>
      </div>
      <ModalFooter
        primaryButton={{
          label: paymentError?.cta || 'Retry',
          onClick: () => handleModalClose(),
          className: 'co-w-full tablet:co-w-auto tablet:co-ml-auto'
        }}
      />
    </Modal>
  );
};
